<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import InboxChannelGlyph from './InboxChannelGlyph.vue';

const props = defineProps({
  triggerDisplayName: {
    type: String,
    required: true,
  },
  currentInboxId: {
    type: [String, Number],
    required: true,
  },
  currentInbox: {
    type: Object,
    default: () => ({}),
  },
  /** When set, uses this list instead of the Vuex store (e.g. Story Dashboard). */
  inboxesList: {
    type: Array,
    default: null,
  },
  /** Opens the channel dropdown on mount (preview only). */
  defaultOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const isOpen = ref(props.defaultOpen);
const rootRef = ref(null);
const triggerRef = ref(null);
const menuRef = ref(null);
const menuPosition = ref({ top: '0px', left: '0px' });

const usesLocalInboxes = computed(() => props.inboxesList !== null);

const inboxes = computed(() => {
  if (usesLocalInboxes.value) return props.inboxesList;
  return store.getters['inboxes/getInboxes'] || [];
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const currentIdNum = computed(() => Number(props.currentInboxId));

const showMenu = computed(() => sortedInboxes.value.length > 1);

const syncMenuPosition = () => {
  if (!triggerRef.value) return;
  const rect = triggerRef.value.getBoundingClientRect();
  const isRtl = document.documentElement.dir === 'rtl';

  menuPosition.value = isRtl
    ? {
        top: `${rect.bottom - 14}px`,
        right: `${window.innerWidth - rect.right + 32}px`,
        left: 'auto',
      }
    : {
        top: `${rect.bottom - 15}px`,
        left: `${rect.left + 42}px`,
      };
};

watch(
  () => props.currentInboxId,
  () => {
    isOpen.value = false;
  }
);

watch(
  () => props.defaultOpen,
  value => {
    isOpen.value = value;
    if (value) syncMenuPosition();
  }
);

const closeMenu = () => {
  isOpen.value = false;
};

const toggleMenu = () => {
  if (!showMenu.value) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) syncMenuPosition();
};

watch(isOpen, open => {
  if (open) {
    syncMenuPosition();
    window.addEventListener('resize', syncMenuPosition);
    window.addEventListener('scroll', syncMenuPosition, true);
    return;
  }
  window.removeEventListener('resize', syncMenuPosition);
  window.removeEventListener('scroll', syncMenuPosition, true);
});

onClickOutside(rootRef, closeMenu, { ignore: [menuRef] });

onBeforeUnmount(() => {
  window.removeEventListener('resize', syncMenuPosition);
  window.removeEventListener('scroll', syncMenuPosition, true);
});

const selectInbox = inbox => {
  if (inbox.id === currentIdNum.value) {
    closeMenu();
    return;
  }
  closeMenu();

  if (usesLocalInboxes.value) {
    emit('select', inbox);
    return;
  }

  const params = {
    accountId: route.params.accountId,
    inboxId: String(inbox.id),
  };
  if (route.params.tab) {
    params.tab = route.params.tab;
  }
  router.push({
    name: 'settings_inbox_show',
    params,
  });
};

const ensureInboxList = () => {
  if (usesLocalInboxes.value) return;
  if (!inboxes.value.length) {
    store.dispatch('inboxes/get');
  }
};

onMounted(() => {
  ensureInboxList();
  if (isOpen.value) syncMenuPosition();
});
</script>

<template>
  <div
    ref="rootRef"
    class="mb-8 w-full overflow-visible border-b border-n-weak pb-4 md:border-0 md:pb-0"
  >
    <div
      v-if="showMenu"
      class="relative flex w-full flex-col items-center gap-0 overflow-visible text-center md:items-start md:text-start"
    >
      <button
        ref="triggerRef"
        type="button"
        class="group flex w-full min-w-0 max-w-none items-start gap-3 rounded-lg px-0.5 py-1 text-start transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-n-blue-9"
        :aria-expanded="isOpen"
        :aria-label="$t('INBOX_MGMT.SETTINGS_NAV.SWITCH_INBOX_ARIA')"
        @click="toggleMenu"
      >
        <InboxChannelGlyph
          class="size-9 shrink-0 self-start"
          :inbox="currentInbox"
          size="md"
        />
        <div class="flex min-w-0 flex-1 flex-col gap-1.5">
          <p
            class="mb-0 truncate text-sm font-semibold leading-tight text-n-slate-12"
          >
            {{ triggerDisplayName }}
          </p>
          <p
            class="mb-0 text-xs leading-tight text-n-slate-11 transition-colors group-hover:text-n-slate-12"
          >
            {{ $t('INBOX_MGMT.SETTINGS_NAV.INBOX_ID', { id: currentInboxId }) }}
          </p>
        </div>
        <Icon
          :icon="isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="size-[22px] shrink-0 self-start text-n-slate-11 transition-colors group-hover:text-n-slate-12"
        />
      </button>

      <Teleport to="body">
        <div
          v-if="isOpen"
          ref="menuRef"
          class="fixed z-[200] flex max-h-64 min-w-[17.5rem] w-max flex-col overflow-y-auto border-0 bg-n-alpha-3 p-0 shadow-lg outline outline-1 outline-n-container backdrop-blur-[100px] dark:outline-n-slate-5"
          :style="menuPosition"
          role="listbox"
          :aria-label="t('INBOX_MGMT.SETTINGS_NAV.INBOX_LIST_ARIA')"
        >
          <button
            v-for="item in sortedInboxes"
            :key="item.id"
            type="button"
            role="option"
            :aria-selected="item.id === currentIdNum"
            class="group flex w-full min-w-0 items-center gap-3 rounded-none px-3 py-2 text-start transition-colors"
            :class="
              item.id === currentIdNum ? 'bg-n-brand/10' : 'hover:bg-n-alpha-2'
            "
            @click="selectInbox(item)"
          >
            <InboxChannelGlyph :inbox="item" size="sm" bare />
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <p
                class="mb-0 truncate text-sm font-medium leading-tight transition-colors"
                :class="
                  item.id === currentIdNum ? 'text-n-brand' : 'text-n-slate-12'
                "
              >
                {{ item.name }}
              </p>
              <p
                class="mb-0 text-xs leading-tight transition-colors"
                :class="
                  item.id === currentIdNum
                    ? 'text-n-brand/80'
                    : 'text-n-slate-11 group-hover:text-n-slate-12'
                "
              >
                {{ $t('INBOX_MGMT.SETTINGS_NAV.INBOX_ID', { id: item.id }) }}
              </p>
            </div>
          </button>
        </div>
      </Teleport>
    </div>

    <div
      v-else
      class="flex w-full flex-col items-center gap-3 overflow-visible text-center md:items-start md:text-start"
    >
      <InboxChannelGlyph
        class="size-9 shrink-0"
        :inbox="currentInbox"
        size="md"
      />
      <div class="flex min-w-0 w-full flex-col gap-1.5">
        <p
          class="mb-0 truncate text-sm font-semibold leading-tight text-n-slate-12"
        >
          {{ triggerDisplayName }}
        </p>
        <p class="mb-0 text-xs leading-tight text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_NAV.INBOX_ID', { id: currentInboxId }) }}
        </p>
      </div>
    </div>
  </div>
</template>
